import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { usePermissions } from "@/hooks/usePermissions";
import { useIsMobile } from "@/hooks/use-mobile";
import { ChevronDown, ChevronUp, BarChart3 } from "lucide-react";
import { VarianceExplanationCard } from "./VarianceExplanationCard";

const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

const fmtSigned = (n: number) =>
  n === 0
    ? "—"
    : (n > 0 ? "+" : "") +
      new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

export interface ProjectKPICardsProps {
  projectId?: string;
  // Card 1 — Total Job Value
  originalContract: number;
  addendums: number;
  deductions: number;
  totalJobValue: number;
  // Card 2 — Budgets & Variances
  sovBudget: number;
  committedCost: number;
  committedVariance: number;
  // Card 3 — Financials AR
  totalInvoiced: number;
  paymentsReceived: number;
  arBalance: number;
  // Card 3 — Financials AP
  totalVendorBilled: number;
  vendorPaid: number;
  paymentsDue: number;
  // Card 4 — Job Profits
  totalProfit: number;
  profitMargin: number;
  baseProfit: number;
  coImpact: number;
  varianceImpact: number;
  isLoading?: boolean;
}

// ── Shared sub-components ────────────────────────────────────────────────────

interface RowProps {
  label: string;
  value: string;
  valueClass?: string;
  labelClass?: string;
  indent?: boolean;
  tooltip?: string;
}

const MetricRow = ({ label, value, valueClass, labelClass, indent, tooltip }: RowProps) => {
  const row = (
    <div className={cn("flex justify-between items-baseline gap-2 py-0.5", indent && "pl-3")}>
      <span className={cn("text-xs text-muted-foreground truncate", labelClass)}>{label}</span>
      <span className={cn("text-xs font-medium tabular-nums whitespace-nowrap", valueClass)}>{value}</span>
    </div>
  );

  if (!tooltip) return row;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="cursor-help">{row}</div>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs text-xs">
        {tooltip}
      </TooltipContent>
    </Tooltip>
  );
};

interface KpiCardProps {
  dotClass: string;
  title: string;
  primaryValue: string;
  primaryClass?: string;
  badge?: React.ReactNode;
  children: React.ReactNode;
  primaryTooltip?: string;
}

const KpiCard = ({ dotClass, title, primaryValue, primaryClass, badge, children, primaryTooltip }: KpiCardProps) => (
  <Card className="border bg-card h-full">
    <CardContent className="pt-4 pb-3 px-4">
      {/* Header */}
      <div className="flex items-center gap-1.5 mb-0.5">
        <span className={cn("h-2 w-2 rounded-full shrink-0", dotClass)} />
        <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide truncate">
          {title}
        </span>
      </div>
      {/* Primary metric */}
      <div className="flex items-baseline gap-2 mb-2">
        {primaryTooltip ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <p className={cn("text-2xl font-bold tracking-tight cursor-help", primaryClass)}>{primaryValue}</p>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-xs text-xs">
              {primaryTooltip}
            </TooltipContent>
          </Tooltip>
        ) : (
          <p className={cn("text-2xl font-bold tracking-tight", primaryClass)}>{primaryValue}</p>
        )}
        {badge}
      </div>
      {/* Divider */}
      <div className="border-t border-border/50 mb-2" />
      {/* Breakdown rows */}
      <div className="space-y-0">{children}</div>
    </CardContent>
  </Card>
);

// ── Color helpers ────────────────────────────────────────────────────────────

/** Variance: positive (under budget) = green; slightly negative = amber; significantly negative = red */
function varianceClass(variance: number, budget: number): string {
  if (variance >= 0) return "text-green-600";
  const pct = budget > 0 ? (Math.abs(variance) / budget) * 100 : 100;
  return pct <= 5 ? "text-amber-600" : "text-red-600";
}

/** Profit margin color */
function marginClass(pct: number): string {
  return pct >= 20 ? "text-green-600" : pct >= 10 ? "text-amber-600" : "text-red-600";
}

// ── Main component ───────────────────────────────────────────────────────────

export const ProjectKPICards = ({
  projectId,
  originalContract, addendums, deductions, totalJobValue,
  sovBudget, committedCost, committedVariance,
  totalInvoiced, paymentsReceived, arBalance,
  totalVendorBilled, vendorPaid, paymentsDue,
  totalProfit, profitMargin, baseProfit, coImpact, varianceImpact,
  isLoading = false,
}: ProjectKPICardsProps) => {
  const { can } = usePermissions();
  const showFinancials = can("view_financials");
  const isMobile = useIsMobile();
  const [expanded, setExpanded] = useState(false);
  const [varianceReportOpen, setVarianceReportOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <Card key={i} className="border bg-card">
            <CardContent className="pt-4 pb-3 px-4">
              <Skeleton className="h-3 w-24 mb-2" />
              <Skeleton className="h-7 w-32 mb-3" />
              <Skeleton className="h-3 w-full mb-1" />
              <Skeleton className="h-3 w-full mb-1" />
              <Skeleton className="h-3 w-3/4" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  const hidden = "—";
  const s = showFinancials; // shorthand

  // ── Card 1: Total Job Value ──────────────────────────────────────────────
  const card1 = (
    <KpiCard
      dotClass="bg-primary"
      title="Total Job Value"
      primaryValue={s ? fmt(totalJobValue) : hidden}
      primaryTooltip={s ? "Original Contract + Approved Change Orders (Addendums − Deductions)" : undefined}
    >
      <MetricRow
        label="Original Contract"
        value={s ? fmt(originalContract) : hidden}
        tooltip={s ? "The finalized contract value before any change orders" : undefined}
      />
      <MetricRow
        label="+ Addendums"
        value={s ? (addendums > 0 ? fmtSigned(addendums) : "—") : hidden}
        valueClass={s && addendums > 0 ? "text-green-600" : undefined}
        tooltip={s ? "Sum of approved scope additions from Change Orders" : undefined}
      />
      <MetricRow
        label="− Deductions"
        value={s ? (deductions > 0 ? fmt(-deductions) : "—") : hidden}
        valueClass={s && deductions > 0 ? "text-red-600" : undefined}
        tooltip={s ? "Sum of approved scope reductions and backcharges" : undefined}
      />
    </KpiCard>
  );

  // ── Card 2: Budgets & Variances ───────────────────────────────────────────
  const varClass = s ? varianceClass(committedVariance, sovBudget) : "text-muted-foreground";
  const varLabel = committedVariance === 0
    ? "On budget"
    : committedVariance > 0
    ? "Under budget"
    : "Over budget";

  const card2Inner = (
    <KpiCard
      dotClass="bg-blue-500"
      title="Budgets & Variances"
      primaryValue={s ? fmt(sovBudget) : hidden}
      primaryClass="text-blue-700 dark:text-blue-400"
      primaryTooltip={s ? "Total estimated subcontractor + material cost across all SOV lines" : undefined}
    >
      <MetricRow
        label="SOV Budget"
        value={s ? fmt(sovBudget) : hidden}
        tooltip={s ? "SUM(quantity × sub_unit_price) for all SOV lines" : undefined}
      />
      <MetricRow
        label="Committed Cost"
        value={s ? fmt(committedCost) : hidden}
        tooltip={s ? "Total value of issued Work Orders + approved Purchase Orders" : undefined}
      />
      <MetricRow
        label={`Variance (${varLabel})`}
        value={s ? fmtSigned(committedVariance) : hidden}
        valueClass={varClass}
        labelClass={varClass}
        tooltip={s ? "SUM((budgeted sub rate − actual WO rate) × assigned qty). Only counts assigned lines." : undefined}
      />
    </KpiCard>
  );

  // Wrap Card 2 with ContextMenu for variance report
  const card2 = projectId ? (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <div>{card2Inner}</div>
      </ContextMenuTrigger>
      <ContextMenuContent>
        <ContextMenuItem
          className="gap-2"
          onClick={() => setVarianceReportOpen(true)}
        >
          <BarChart3 className="h-4 w-4" />
          Generate Variance Analysis
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  ) : card2Inner;

  // ── Card 3: Financials ────────────────────────────────────────────────────
  const card3 = (
    <Card className="border bg-card h-full">
      <CardContent className="pt-4 pb-3 px-4">
        {/* AR Section */}
        <div className="flex items-center gap-1.5 mb-0.5">
          <span className="h-2 w-2 rounded-full shrink-0 bg-emerald-500" />
          <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">
            Financials
          </span>
        </div>
        <p className="text-[10px] font-semibold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider mt-2 mb-1">
          AR — Receivables
        </p>
        <MetricRow
          label="Total Invoiced"
          value={s ? fmt(totalInvoiced) : hidden}
          tooltip={s ? "Sum of all approved/sent invoices for this project" : undefined}
        />
        <MetricRow
          label="Payments Received"
          value={s ? fmt(paymentsReceived) : hidden}
          valueClass="text-green-600"
          tooltip={s ? "Total payments collected from the client" : undefined}
        />
        <MetricRow
          label="Current Balance"
          value={s ? fmt(arBalance) : hidden}
          valueClass={s && arBalance > 0 ? "text-amber-600" : undefined}
          tooltip={s ? "Invoiced − Payments Received (outstanding receivables)" : undefined}
        />

        {/* Divider */}
        <div className="border-t border-border/50 my-2" />

        {/* AP Section */}
        <p className="text-[10px] font-semibold text-orange-600 dark:text-orange-400 uppercase tracking-wider mb-1">
          AP — Payables
        </p>
        <MetricRow
          label="Total Vendor Billed"
          value={s ? fmt(totalVendorBilled) : hidden}
          tooltip={s ? "Sum of all approved vendor bills against this project" : undefined}
        />
        <MetricRow
          label="Paid to Vendors"
          value={s ? fmt(vendorPaid) : hidden}
          valueClass="text-green-600"
          tooltip={s ? "Total payments made to vendors/subcontractors" : undefined}
        />
        <MetricRow
          label="Payments Due"
          value={s ? fmt(paymentsDue) : hidden}
          valueClass={s && paymentsDue > 0 ? "text-red-600" : undefined}
          tooltip={s ? "Vendor Billed − Paid (outstanding payables)" : undefined}
        />
      </CardContent>
    </Card>
  );

  // ── Card 4: Job Profits ───────────────────────────────────────────────────
  const profitColor = s ? marginClass(profitMargin) : "text-muted-foreground";
  const profitDot   = profitMargin >= 20 ? "bg-green-500" : profitMargin >= 10 ? "bg-amber-500" : "bg-red-500";

  const card4 = (
    <KpiCard
      dotClass={profitDot}
      title="Job Profits"
      primaryValue={s ? fmt(totalProfit) : hidden}
      primaryClass={profitColor}
      primaryTooltip={s ? "Total Job Value − Committed Cost" : undefined}
      badge={
        s ? (
          <span className={cn("text-sm font-semibold", profitColor)}>
            ({profitMargin.toFixed(1)}%)
          </span>
        ) : undefined
      }
    >
      <MetricRow
        label="Base Profit"
        value={s ? fmtSigned(baseProfit) : hidden}
        valueClass={s ? (baseProfit >= 0 ? "text-green-600" : "text-red-600") : undefined}
        tooltip={s ? "Original Contract Value − SOV Budget (estimated cost)" : undefined}
      />
      <MetricRow
        label="CO Impact"
        value={s ? (coImpact !== 0 ? fmtSigned(coImpact) : "—") : hidden}
        valueClass={s && coImpact !== 0 ? (coImpact > 0 ? "text-green-600" : "text-red-600") : undefined}
        tooltip={s ? "Net impact of Change Orders (Addendums − Deductions)" : undefined}
      />
      <MetricRow
        label="Variance Impact"
        value={s ? (varianceImpact !== 0 ? fmtSigned(varianceImpact) : "—") : hidden}
        valueClass={s && varianceImpact !== 0 ? (varianceImpact >= 0 ? "text-green-600" : "text-red-600") : undefined}
        tooltip={s ? "Savings or overruns from sub rates vs. budgeted rates on assigned work" : undefined}
      />
    </KpiCard>
  );

  const allCards = [
    { key: "job-value", node: card1 },
    { key: "budgets",   node: card2 },
    { key: "financials",node: card3 },
    { key: "profits",   node: card4 },
  ];
  const MOBILE_PRIORITY = [0, 3]; // Card 1 + Card 4 on mobile
  const visibleCards = isMobile && !expanded
    ? MOBILE_PRIORITY.map((i) => allCards[i])
    : allCards;

  return (
    <TooltipProvider delayDuration={300}>
      <div>
        <div className={cn(
          "grid gap-3",
          isMobile ? "grid-cols-2" : "grid-cols-2 lg:grid-cols-4"
        )}>
          {visibleCards.map(({ key, node }) => (
            <div key={key}>{node}</div>
          ))}
        </div>
        {isMobile && (
          <Button
            variant="ghost"
            size="sm"
            className="w-full mt-1 text-xs text-muted-foreground"
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? (
              <><ChevronUp className="h-3 w-3 mr-1" /> Show less</>
            ) : (
              <><ChevronDown className="h-3 w-3 mr-1" /> Show all 4 KPI cards</>
            )}
          </Button>
        )}
      </div>

      {/* Variance Analysis Dialog */}
      {projectId && (
        <Dialog open={varianceReportOpen} onOpenChange={setVarianceReportOpen}>
          <DialogContent className="w-[95vw] max-w-5xl max-h-[85vh] overflow-y-auto p-4 sm:p-6">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />
                Variance Analysis Report
              </DialogTitle>
              <DialogDescription>
                Detailed breakdown of cost variances between budgeted and actual subcontractor rates.
              </DialogDescription>
            </DialogHeader>
            <VarianceExplanationCard projectId={projectId} />
          </DialogContent>
        </Dialog>
      )}
    </TooltipProvider>
  );
};
